import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useUser } from "./Context/UserContext";
import { useTheme } from "./Context/ThemeContext";
import Logo from "./ui/Logo";
import Icon from "./ui/Icon";
import BrandCanvas from "./ui/BrandCanvas";
import { entrance, gsap, prefersReducedMotion } from "../lib/motion";
import "../index.css";
import "../styles/login.css";

export default function Login() {
    const { loginUser } = useUser();
    const { theme, toggleTheme } = useTheme();

    const [empIdInput, setEmpIdInput] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [empIdError, setEmpIdError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [canvasOn, setCanvasOn] = useState(false);

    const isMounted = useRef(true);
    const pageRef = useRef(null);

    useEffect(() => {
        return () => {
            isMounted.current = false; // cleanup on unmount
        };
    }, []);

    // Arriving here from a signed-out app page inherits that page's scroll
    // offset, which lifts the whole card (and the Sign in button) off centre.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Signature GSAP entrance — brand panel first, then the form cascades in.
    // Purely opacity/transform, killed on unmount, skipped under reduced motion.
    useEffect(() => {
        const root = pageRef.current;
        if (!root) return undefined;
        const tl = entrance([
            { targets: root.querySelectorAll(".login-brand-content > *"), y: 22, stagger: 0.09, at: 0 },
            { targets: root.querySelectorAll(".login-form-head, .login-field, .login-forgot, .login-submit, .login-footer"), y: 16, stagger: 0.07, at: 0.18 },
        ]);
        return () => {
            if (tl) tl.kill();
        };
    }, []);

    // Gentle mouse parallax on the brand panel (desktop pointers only).
    // Constellation drifts with the cursor, content counters it slightly.
    useEffect(() => {
        if (prefersReducedMotion()) return undefined;
        if (!window.matchMedia || !window.matchMedia("(pointer: fine)").matches) return undefined;

        const card = pageRef.current && pageRef.current.querySelector(".login-card");
        const canvasHost = pageRef.current && pageRef.current.querySelector(".brand-canvas-host");
        const content = pageRef.current && pageRef.current.querySelector(".login-brand-content");
        if (!card || !canvasHost || !content) return undefined;

        const canvasX = gsap.quickTo(canvasHost, "x", { duration: 0.8, ease: "power3.out" });
        const canvasY = gsap.quickTo(canvasHost, "y", { duration: 0.8, ease: "power3.out" });
        const contentX = gsap.quickTo(content, "x", { duration: 0.9, ease: "power3.out" });
        const contentY = gsap.quickTo(content, "y", { duration: 0.9, ease: "power3.out" });

        const onMove = e => {
            const r = card.getBoundingClientRect();
            const nx = (e.clientX - r.left) / r.width - 0.5;   // -0.5 … 0.5
            const ny = (e.clientY - r.top) / r.height - 0.5;
            canvasX(nx * 14);
            canvasY(ny * 10);
            contentX(nx * -6);
            contentY(ny * -4);
        };

        const onLeave = () => {
            canvasX(0); canvasY(0); contentX(0); contentY(0);
        };

        card.addEventListener("mousemove", onMove);
        card.addEventListener("mouseleave", onLeave);
        return () => {
            card.removeEventListener("mousemove", onMove);
            card.removeEventListener("mouseleave", onLeave);
            gsap.set([canvasHost, content], { clearProps: "transform" });
        };
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setEmpIdError("");
        setPasswordError("");

        let isValid = true;

        if (!empIdInput.trim()) {
            setEmpIdError("Employee ID / User ID is required.");
            isValid = false;
        }

        if (!password.trim()) {
            setPasswordError("Password is required.");
            isValid = false;
        }

        if (!isValid) {
            return;
        }

        setLoading(true);

        try {
            // Call your backend login API
            const res = await axios.post("/api/auth/login", {
                empId: empIdInput,
                password,
            });

            const { token, empId, menus, active, roles, name, email, doj, reportingMgrId, reportingMgrName } = res.data;


            if (!active) {
                if (isMounted.current) setError("Your account is not active.");
                return;
            }

            // Construct the userInfo object
            const userInfo = {
                name: name || "",              // Employee full name
                email: email || "",
                doj: doj || "",
                roles: roles || [],            // array of role names
                reportingMgrId: reportingMgrId || "",
                reportingMgrName: reportingMgrName || "",
            };

            // Pass userInfo to context
            loginUser(token, empId, menus, userInfo);

        } catch (err) {
            if (isMounted.current) {
                const status = err.response?.status;

                // Every non-credential failure used to collapse into one vague
                // message, which hid real outages (a database being unreachable
                // looked identical to a wrong password). Log the actual status
                // so the cause is visible in the console, and tell the user
                // whether the problem is theirs to fix or ours.
                console.error(
                    "Login failed:",
                    status ? `HTTP ${status}` : "no response from server (network or CORS)",
                    err.response?.data ?? err.message
                );

                switch (status) {
                    case 401:
                        setError("Invalid Employee ID/User ID or password.");
                        break;

                    case 423:
                        setError("Your account has been temporarily locked due to multiple failed login attempts. Please try again after 15 minutes or contact the administrator if you need assistance.");
                        break;

                    case 429:
                        setError("Too many login attempts. Please wait a few minutes before trying again.");
                        break;

                    case 503:
                        setError(
                            err.response?.data?.message ||
                            "Sign-in is temporarily unavailable because a required service could not be reached. Your credentials were not the problem. Please contact IT support if this persists."
                        );
                        break;

                    case undefined:
                        setError("Could not reach the server. Check that the application is running and that you are connected to the network.");
                        break;

                    default:
                        setError("Unable to sign in at the moment. Please try again later.");
                        break;
                }
            }

        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    return (
        <main className="login-page" role="main" id="main-content" ref={pageRef}>
            <button
                type="button"
                className="icon-btn login-theme-toggle"
                onClick={toggleTheme}
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
                <Icon name={theme === "dark" ? "sun" : "moon"} size={19} />
            </button>

            <div className="login-card">
                {/* Left brand panel — the one "signature moment" */}
                <div className="login-brand" aria-hidden="true">
                    {/* Three.js constellation (lazy). CSS orbit stays as fallback
                        until WebGL is ready, and permanently under reduced motion. */}
                    <BrandCanvas onReady={() => setCanvasOn(true)} />

                    <div className={`login-brand-orbit ${canvasOn ? "orbit-muted" : ""}`}>
                        <span className="orbit-dot d1" />
                        <span className="orbit-dot d2" />
                        <span className="orbit-dot d3" />
                        <span className="orbit-dot d4" />
                        <span className="orbit-dot d5" />
                        <span className="orbit-dot d6" />
                        <svg className="orbit-lines" viewBox="0 0 400 400" fill="none">
                            <circle cx="200" cy="200" r="90" />
                            <circle cx="200" cy="200" r="150" />
                            <circle cx="200" cy="200" r="205" />
                        </svg>
                    </div>

                    <div className="login-brand-content">
                        <Logo size={34} tone="light" showWordmark={false} />
                        <h2 className="login-brand-title">
                            Skill Management<span className="login-brand-dot">.</span>
                        </h2>
                        <p className="login-brand-copy">
                            Track competencies, close skill gaps, and grow your team —
                            one place for every capability at Mindteck.
                        </p>

                        <ul className="login-brand-points">
                            <li><Icon name="target" size={15} /> Self &amp; manager assessments</li>
                            <li><Icon name="trendingUp" size={15} /> Team competency insights</li>
                            <li><Icon name="award" size={15} /> Certifications &amp; training</li>
                        </ul>
                    </div>
                </div>

                {/* Right form panel */}
                <div className="login-form-panel">
                    <div className="login-form-head">
                        <span className="page-eyebrow">Welcome back</span>
                        <h1 className="login-title">Sign in</h1>
                        <p className="login-sub">
                            Use your <strong>EMP ID / User ID</strong> and <strong>TMS password</strong>
                        </p>
                    </div>

                    {error && (
                        <div className="notice notice-error" role="alert">
                            <Icon name="alertCircle" size={17} />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleLogin} noValidate>
                        <div className="login-field">
                            <label className="field-label" htmlFor="login-empid">
                                Employee ID / User ID
                            </label>
                            <input
                                id="login-empid"
                                type="text"
                                className={`input-mt ${empIdError ? "input-invalid" : ""}`}
                                value={empIdInput}
                                autoComplete="username"
                                onChange={(e) => {
                                    setEmpIdInput(e.target.value);
                                    setEmpIdError("");
                                    setError("");
                                }}
                                onBlur={() => {
                                    if (!empIdInput.trim()) {
                                        setEmpIdError("Employee ID / User ID is required.");
                                    }
                                }}
                                placeholder="e.g. MT01234"
                                aria-invalid={!!empIdError}
                                aria-describedby={empIdError ? "login-empid-error" : undefined}
                            />
                            {empIdError && (
                                <p id="login-empid-error" className="field-error">{empIdError}</p>
                            )}
                        </div>

                        <div className="login-field">
                            <label className="field-label" htmlFor="login-password">
                                Password
                            </label>
                            <div className="login-password-wrap">
                                <input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    className={`input-mt ${passwordError ? "input-invalid" : ""}`}
                                    value={password}
                                    autoComplete="current-password"
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError("");
                                        setError("");
                                    }}
                                    onBlur={() => {
                                        if (!password.trim()) {
                                            setPasswordError("Password is required.");
                                        }
                                    }}
                                    placeholder="Enter your password"
                                    aria-invalid={!!passwordError}
                                    aria-describedby={passwordError ? "login-password-error" : undefined}
                                />
                                <button
                                    type="button"
                                    className="password-eye"
                                    onClick={() => setShowPassword(s => !s)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                            {passwordError && (
                                <p id="login-password-error" className="field-error">{passwordError}</p>
                            )}
                        </div>

                        <div className="login-forgot">
                            <a
                                href="https://tms.mindteck.com/Settings/ForgotPassword.aspx"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Forgot password?
                            </a>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary-mt login-submit"
                        >
                            {loading && <span className="login-spinner spin" aria-hidden="true" />}
                            <span>{loading ? "Signing in…" : "Sign in"}</span>
                            {!loading && <Icon name="chevronRight" size={17} className="login-submit-arrow" />}
                        </button>
                    </form>

                    <p className="login-footer">
                        © {new Date().getFullYear()} Mindteck · Skill Management Portal
                    </p>
                </div>
            </div>
        </main>
    );
}
